"use client"

import type React from "react"
import { useState, useRef } from "react"
import { useRouter } from "next/navigation"
import LocationAutocomplete from "@/components/LocationAutocomplete"
import { Loader2, ChevronDown } from "lucide-react"
import { trackEvent } from "@/utils/analytics"
import { Turnstile } from "@marsidev/react-turnstile"

const PROPERTY_TYPES = [
  { value: "single_family", label: "Single Family Home" },
  { value: "condo", label: "Condo / Townhouse" },
  { value: "apartment", label: "Apartment" },
  { value: "commercial", label: "Commercial Property" },
]

const inputCls =
  "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
const labelCls = "block text-sm font-medium text-gray-700 mb-1"
const optionalBadge = <span className="ml-1 text-xs font-normal text-gray-400">(optional)</span>

export default function HomeownerRegisterForm({ initialRefCode }: { initialRefCode?: string }) {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const loadedAt = useRef(Date.now())
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    location: "",
    propertyType: "",
    ownershipStatus: "",
    yearBuilt: "",
    squareFootage: "",
    bedrooms: "",
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match")
      setIsLoading(false)
      return
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters")
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          location: formData.location,
          userType: "homeowner",
          refCode: initialRefCode ?? "",
          propertyType: formData.propertyType || undefined,
          ownershipStatus: formData.ownershipStatus || undefined,
          yearBuilt: formData.yearBuilt ? parseInt(formData.yearBuilt) : undefined,
          squareFootage: formData.squareFootage ? parseInt(formData.squareFootage) : undefined,
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
          _hp: "",
          _t: loadedAt.current,
          turnstileToken,
        }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.message || "Registration failed")

      trackEvent("user_registered", {
        user_type: "homeowner",
        location: formData.location,
        property_type: formData.propertyType || undefined,
        ownership_status: formData.ownershipStatus || undefined,
      })

      // Fire GTM/GA4 sign_up event
      ;(window as any).dataLayer = (window as any).dataLayer || []
      ;(window as any).dataLayer.push({ event: "sign_up", user_type: "homeowner" })

      router.push("/login?registered=true")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed")
    } finally {
      setIsLoading(false)
    }
  }

  const currentYear = new Date().getFullYear()

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot — hidden from humans, bots fill it */}
      <input type="text" name="_hp" style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {/* ── Account fields ── */}
      <div>
        <label htmlFor="name" className={labelCls}>Full Name</label>
        <input id="name" name="name" type="text" required value={formData.name}
          onChange={handleChange} className={inputCls} disabled={isLoading} />
      </div>

      <div>
        <label htmlFor="email" className={labelCls}>Email</label>
        <input id="email" name="email" type="email" required value={formData.email}
          onChange={handleChange} className={inputCls} disabled={isLoading} />
      </div>

      <div>
        <label htmlFor="password" className={labelCls}>Password</label>
        <input id="password" name="password" type="password" required value={formData.password}
          onChange={handleChange} className={inputCls} disabled={isLoading} />
        <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
      </div>

      <div>
        <label htmlFor="confirmPassword" className={labelCls}>Confirm Password</label>
        <input id="confirmPassword" name="confirmPassword" type="password" required
          value={formData.confirmPassword} onChange={handleChange}
          className={inputCls} disabled={isLoading} />
      </div>

      {/* ── Location (required) ── */}
      <div>
        <label className={labelCls}>Location</label>
        <LocationAutocomplete
          value={formData.location}
          onChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
          placeholder="Enter your city"
          required
        />
      </div>

      {/* ── Divider ── */}
      <div className="pt-1">
        <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">
          Help us match you with the right tradespeople — all fields below are optional.
        </p>
      </div>

      {/* ── Property type ── */}
      <div>
        <label htmlFor="propertyType" className={labelCls}>
          Property Type {optionalBadge}
        </label>
        <div className="relative">
          <select
            id="propertyType"
            name="propertyType"
            value={formData.propertyType}
            onChange={handleChange}
            className={`${inputCls} appearance-none pr-8`}
            disabled={isLoading}
          >
            <option value="">Select property type…</option>
            {PROPERTY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-gray-400" />
        </div>
      </div>

      {/* ── Own or rent ── */}
      <div>
        <label className={labelCls}>
          Do you own or rent? {optionalBadge}
        </label>
        <div className="flex gap-3">
          {[
            { value: "own", label: "I own" },
            { value: "rent", label: "I rent" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={isLoading}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  ownershipStatus: prev.ownershipStatus === opt.value ? "" : opt.value,
                }))
              }
              className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${
                formData.ownershipStatus === opt.value
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "border-gray-300 text-gray-700 hover:border-blue-400"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {formData.ownershipStatus === "rent" && (
          <p className="mt-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Renters may need landlord approval for some types of work.
          </p>
        )}
      </div>

      {/* ── Year built ── */}
      <div>
        <label htmlFor="yearBuilt" className={labelCls}>
          Year Built {optionalBadge}
        </label>
        <input
          id="yearBuilt"
          name="yearBuilt"
          type="number"
          min={1800}
          max={currentYear}
          placeholder={`e.g. 1985`}
          value={formData.yearBuilt}
          onChange={handleChange}
          className={inputCls}
          disabled={isLoading}
        />
        {formData.yearBuilt && parseInt(formData.yearBuilt) < 1978 && (
          <p className="mt-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Homes built before 1978 may contain lead paint — tradespeople will be flagged accordingly.
          </p>
        )}
        {formData.yearBuilt && parseInt(formData.yearBuilt) < 1950 && (
          <p className="mt-1 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Older homes may have knob-and-tube wiring or other legacy systems.
          </p>
        )}
      </div>

      {/* ── Sq ft + bedrooms side by side ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="squareFootage" className={labelCls}>
            Sq. Footage {optionalBadge}
          </label>
          <input
            id="squareFootage"
            name="squareFootage"
            type="number"
            min={100}
            placeholder="e.g. 1800"
            value={formData.squareFootage}
            onChange={handleChange}
            className={inputCls}
            disabled={isLoading}
          />
        </div>
        <div>
          <label htmlFor="bedrooms" className={labelCls}>
            Bedrooms {optionalBadge}
          </label>
          <input
            id="bedrooms"
            name="bedrooms"
            type="number"
            min={0}
            max={20}
            placeholder="e.g. 3"
            value={formData.bedrooms}
            onChange={handleChange}
            className={inputCls}
            disabled={isLoading}
          />
        </div>
      </div>

      {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
        <Turnstile
          siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
          onSuccess={setTurnstileToken}
          options={{ theme: "light" }}
        />
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-md text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading || (!!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken)}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <><Loader2 className="animate-spin mr-2 h-4 w-4" />Registering…</>
        ) : (
          "Create Account"
        )}
      </button>
    </form>
  )
}
